import { ROLLOUT_NEW_SIGNUP_FLOW_PERCENTAGE } from '@libs/utils/environments'
import { useState, useCallback } from 'react'

type ClientRolloutKey = 'new_signup_flow_assignment'

export type CachedRolloutState = {
  isFeatureEnabledInCache: boolean | null
  cachedRolloutThreshold: number | null
}

const useSessionStorage = <T>(key: string, defaultValue: T) => {
  const [value, setValue] = useState<T>(() => {
    const storedValue = sessionStorage.getItem(key)
    return storedValue ? JSON.parse(storedValue) : defaultValue
  })

  const updateValue = useCallback(
    (newValue: T) => {
      try {
        sessionStorage.setItem(key, JSON.stringify(newValue))
      } catch (error) {
        // Ignore potential storage errors in this hook
      }
      setValue(newValue)
    },
    [key]
  )

  return [value, updateValue] as [T, (value: T) => void]
}

const isUserInRollout = (percentage: number): boolean => {
  const random = Math.random()
  const threshold = percentage / 100
  return random < threshold
}

const useClientRolloutStorage = (key: ClientRolloutKey) => {
  const [rollout, setRollout] = useSessionStorage<CachedRolloutState>(key, {
    isFeatureEnabledInCache: null,
    cachedRolloutThreshold: null
  })

  return { rollout, setRollout }
}

export type UseClientRolloutProps = {
  key: ClientRolloutKey
  rolloutPercentage: number
}

export const useClientRollout = ({
  key,
  rolloutPercentage
}: UseClientRolloutProps): {
  isEnabled: boolean
} => {
  const { rollout, setRollout } = useClientRolloutStorage(key)

  if (rolloutPercentage <= 0 || !rolloutPercentage) {
    return { isEnabled: false }
  }
  if (rolloutPercentage >= 100) {
    return { isEnabled: true }
  }

  const { isFeatureEnabledInCache, cachedRolloutThreshold } = rollout

  if (isFeatureEnabledInCache === true) {
    return { isEnabled: true }
  }
  if (isFeatureEnabledInCache === false) {
    if (rolloutPercentage === cachedRolloutThreshold) {
      return { isEnabled: false }
    }
  }

  const isEnabled = isUserInRollout(rolloutPercentage)
  setRollout({
    isFeatureEnabledInCache: isEnabled,
    cachedRolloutThreshold: rolloutPercentage
  })
  return { isEnabled }
}

export const useNewFlowAssignementStorage = () => {
  return useClientRolloutStorage('new_signup_flow_assignment')
}

export const useNewFlowAssignmentRollout = () => {
  return useClientRollout({
    key: 'new_signup_flow_assignment',
    rolloutPercentage: Number(ROLLOUT_NEW_SIGNUP_FLOW_PERCENTAGE)
  })
}

export { isUserInRollout }
